import InfoBar from '@components/V4/InfoBar';
import { useAuthState } from '@contexts/AuthContext';
import { DISCORD_MENTOR_CHANNEL, DISCORD_STUDENT_CHANNEL } from '@shared/constants';

type DeviceKind = 'camera' | 'microphone';

type Props = {
  device: DeviceKind;
  hasRetry?: boolean;
};

const DEVICE_LABELS: Record<DeviceKind, { noun: string; apps: string; os: string }> = {
  camera: {
    noun: 'camera',
    apps: 'Zoom, Teams, Skype, Discord, OBS or another browser tab',
    os: 'Camera',
  },
  microphone: {
    noun: 'microphone',
    apps: 'Zoom, Teams, Skype, Discord or another browser tab',
    os: 'Microphone',
  },
};

const DeviceTroubleshooting = ({ device, hasRetry = true }: Props) => {
  const { isMentor } = useAuthState();
  const { noun, apps, os } = DEVICE_LABELS[device];
  const supportChannel = isMentor ? DISCORD_MENTOR_CHANNEL : DISCORD_STUDENT_CHANNEL;

  return (
    <InfoBar
      variant="warning"
      messageTitle={`Your ${noun} could not be started`}
      messageBody={`You can still join the meeting without ${
        device === 'camera' ? 'video' : 'audio'
      }. If you want to use your ${noun}, try the steps below${
        hasRetry ? ', then click Retry' : ', then reload the page'
      }:`}
    >
      <ul className="list-disc pl-4 text-xs leading-5 flex flex-col gap-1">
        <li>
          Close any other app that may be using the {noun} ({apps}).
        </li>
        <li>
          Check the site permission in the browser (the lock icon next to the address) and make sure
          the {noun} is allowed, not blocked.
        </li>
        <li>
          Windows: Settings → Privacy &amp; security → {os}: turn on &quot;{os} access&quot; and
          &quot;Let desktop apps access your {noun}&quot;. macOS: System Settings → Privacy &amp;
          Security → {os}: allow your browser.
        </li>
        <li>
          Antivirus or security software (Kaspersky, Bitdefender, ESET, Norton, McAfee) often has a
          webcam/microphone protection feature that blocks browsers; allow the browser there or
          disable the protection temporarily.
        </li>
        {device === 'camera' && (
          <li>
            Check the physical privacy shutter or the camera kill switch (often an Fn key) on the
            laptop.
          </li>
        )}
        <li>
          Make sure the {noun} is enabled and has working drivers (Windows: Device Manager →{' '}
          {device === 'camera' ? 'Cameras' : 'Audio inputs and outputs'}). For an external {noun},
          unplug it and plug it back in.
        </li>
        <li>Try another browser (Google Chrome or Microsoft Edge) or restart the computer.</li>
      </ul>
      <p className="text-xs leading-5">
        Still not working? Reach out on{' '}
        <a href={supportChannel} target="_blank" rel="noreferrer" className="underline">
          #agora-support
        </a>
        .
      </p>
    </InfoBar>
  );
};

export default DeviceTroubleshooting;
